'use strict';

import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { createUpdateApplicant, createSaveApplication, getYtjInformation } from '../../actions/application';
import { GetFetchOffer } from '../../actions/loanOffer';
import { createLoginToken } from '../../actions/login';
import { trackEvent } from '../../utils/gtm';
import BankSelectionPanel from '../BankSelectionPanel';
import { AMOUNT, AMOUNT_KNEE, LENGTH, FREE_MONTHS, buildAttribution, campaignCodeFromUrl } from './applicationContext';
import { normalizeBic } from './bic';
import { Tick, TrustChips, CHIPS } from './Trust';
import { TERMS_URL } from './PageFrame';
import {
  AUTH_BYPASS, AUTH_CONTEXT_APPLICATION, fmt, validators, ERROR_TEXT, SAVE_FAILED_PHONE_MSG,
  resolveSavedApplicationId, focusAndScrollToFieldError,
  Range, KneeRange, Switch, Consent, Assurance, StepsBar, AttachmentsBlock, AuthButton, StickyCta, SuccessIcon, ArrowIcon, LockIcon,
  CompanyContactFields, CampaignCode, useProcessStarted, useStickyVisible, useAttachments, useBusinessId,
} from './pieces';

/**
 * Yrityslaina, two steps.
 *
 * Step 1  loan parameters + company & contact details → saves the application
 *         (same point the live flow does) and moves on
 * Step 2  optional attachments, then strong authentication (bank ids via
 *         BankSelectionPanel, or Mobiilivarmenne directly) → saves again with
 *         attachments and hands the browser to the identity provider
 *
 * loanApplicationLoanTypeEnum 1 (regular loan).
 */

const DEBOUNCE_MS = 300;
const AI_FAST_TRACK_MAX = 10000; // same rule as the live flow

const offerText = (offer) => {
  const d = offer && offer.offerdata && offer.offerdata.loanDetails;
  const loading = offer && offer.offerdata && offer.offerdata.isFetching && !d;
  return {
    monthly: loading || !d ? '…' : `n. ${fmt(d.monthlyPmt)} €/kk`,
    total: loading || !d ? '…' : `n. ${fmt(d.totalPmt)} €`,
    monthlyPmt: d ? d.monthlyPmt : 0,
  };
};

function LoanSliders({ loan, onChange }) {
  return (
    <>
      <KneeRange label="Lainasumma" value={loan.amount} range={AMOUNT} knee={AMOUNT_KNEE} unit="€" big format={fmt} ends={['2 000 €', '250 000 €']} onChange={(v) => onChange({ amount: v })} />
      <Range label="Laina-aika" value={loan.length} range={LENGTH} unit="kk" ends={['1 kk', '48 kk']} onChange={(v) => onChange({ length: v })} />
      <Range label="Lyhennysvapaat kuukaudet" value={loan.free} range={FREE_MONTHS} unit="kk" ends={['0 kk', '3 kk']} onChange={(v) => onChange({ free: v })} />
    </>
  );
}

function AiFastTrackChip({ amount }) {
  if (amount > AI_FAST_TRACK_MAX) return null;
  return <div className="kr-ai-chip">⚡ Hakemus käsitellään AI-avusteisesti, voit saada lainatarjouksen minuutissa</div>;
}

function BecomeApplicantV2Process({ context, application, offer, updateApplication, getOffer, fetchYtj, saveApplication, requestLoginToken, onStepChange }) {
  const prefilled = context.mode === 'prefilled';

  const [step, setStep] = useState(1); // 1 | 2 | 'done'
  // The page frame hides the cross-sell text under the form once the user has left step 1.
  useEffect(() => { if (onStepChange) onStepChange(step); }, [step, onStepChange]);
  const [loan, setLoan] = useState(context.loan);
  const [consolidation, setConsolidation] = useState(context.consolidation);
  const urlCode = campaignCodeFromUrl(context.values); // ?koodi=… prefills the campaign code
  const [fields, setFields] = useState({ bic: '', companyName: '', phone: '', email: '', discountCode: urlCode });
  const [touched, setTouched] = useState({});
  const [terms, setTerms] = useState(false);
  const [showCampaign, setShowCampaign] = useState(Boolean(urlCode));
  const [showAttachments, setShowAttachments] = useState(false);
  const [editingLoan, setEditingLoan] = useState(!prefilled); // no URL parameters → sliders open from the start
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [loadingServiceId, setLoadingServiceId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const attribution = useRef(null);
  const cardRef = useRef(null);
  const completed = useRef(false);
  const processName = consolidation ? 'combine_loans' : 'loan';
  const markStarted = useProcessStarted(processName);
  const stickyVisible = useStickyVisible(cardRef, step);
  const { attachments, add: addFiles, remove: removeFile } = useAttachments(setSubmitError);
  const bic = useBusinessId({ fields, setFields, setErrors, application, updateApplication, fetchYtj });

  // Mount: resolve marketing attribution once, seed Redux, fetch the first offer.
  useEffect(() => {
    attribution.current = buildAttribution(context.values);
    updateApplication({
      loanAmount: loan.amount, loanLength: loan.length, capitalFreeMonths: loan.free,
      loanApplicationLoanTypeEnum: 1, loanApplicationTypeEnum: consolidation ? 1 : 0, ...attribution.current,
    });
    getOffer(loan.amount, loan.length, 0, loan.free);
  }, []); // mount only — intentionally not re-run on later state changes

  // Recalculate the offer when loan parameters change (debounced like the live flow).
  const firstOffer = useRef(true);
  useEffect(() => {
    if (firstOffer.current) { firstOffer.current = false; return undefined; }
    const t = setTimeout(() => getOffer(loan.amount, loan.length, 0, loan.free), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [loan.amount, loan.length, loan.free, getOffer]);

  const changeLoan = (patch) => { markStarted(); setLoan((l) => ({ ...l, ...patch })); };
  const setField = (name) => (e) => {
    markStarted();
    const value = name === 'bic' ? bic.onChange(e) : e.target.value;
    setFields((f) => ({ ...f, [name]: value }));
    if (errors[name] && (!validators[name] || validators[name](value))) setErrors((er) => ({ ...er, [name]: undefined }));
  };
  const blurField = (name) => () => {
    setTouched((t) => ({ ...t, [name]: true }));
    if (name === 'bic') bic.onBlurNormalize();
    if (fields[name].trim() !== '' && validators[name] && !validators[name](fields[name])) {
      setErrors((er) => ({ ...er, [name]: ERROR_TEXT[name] }));
      trackEvent('field_error', { field_id: name, step: 1 });
    }
  };
  const isValid = (name) => touched[name] && validators[name] && validators[name](fields[name]);

  const { monthlyPmt, monthly, total } = offerText(offer);

  // The API has only POST: a body without an id creates a new application, a body with the
  // id of an earlier save updates it. The live flow resends the id via Redux state; do the
  // same here so the step-1 save, the pre-authentication save and any back-and-forth all
  // land on one application instead of several.
  const buildPayload = () => ({
    ...(savedId ? { id: savedId } : {}),
    loanAmount: loan.amount,
    loanLength: loan.length,
    capitalFreeMonths: loan.free,
    LoanPurposeTypeEnum: 0,
    LoanGuaranteeTypeEnum: 0,
    LoanDueDateTypeEnum: 0,
    freeMonthsToPaymentsCheck: false,
    ApplicationSource: '',
    loanApplicationTypeEnum: consolidation ? 1 : 0,
    loanApplicationLoanTypeEnum: 1, // regular loan
    bic: normalizeBic(fields.bic),
    companyName: fields.companyName.trim(),
    companySsnBic: application.companySsnBic,
    businessLine: application.businessLine,
    address: application.address,
    addressCo: application.addressCo,
    zip: application.zip,
    city: application.city,
    phone: fields.phone.trim(),
    email: fields.email.trim(),
    discountCode: fields.discountCode.trim(),
    monthlyPayment: monthlyPmt || 0,
    ...(attribution.current || {}),
  });

  const validateStep1 = () => {
    const next = {};
    Object.keys(validators).forEach((k) => { if (!validators[k](fields[k])) next[k] = ERROR_TEXT[k]; });
    if (!terms) next.terms = ERROR_TEXT.terms;
    setErrors(next);
    setTouched({ bic: true, companyName: true, phone: true, email: true });
    const firstBad = Object.keys(next)[0];
    if (firstBad) {
      trackEvent('form_submit_attempt', { step: 1, ok: false });
      focusAndScrollToFieldError(firstBad);
    }
    return !firstBad;
  };

  const scrollToCard = () => cardRef.current && cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Step 1 → 2. The live flow saves the application here already; keep that.
  const goStep2 = async () => {
    if (busy || !validateStep1()) return;
    setBusy(true);
    setSubmitError(null);
    const payload = buildPayload();
    updateApplication(payload);
    try {
      const saved = await saveApplication(payload);
      setSavedId(saved.id);
      if (saved.loanAmount > 0 && !completed.current) {
        completed.current = true; // going back and forward again must not report a second conversion
        trackEvent('kr_process_completed', { process_name: processName, value: saved.loanAmount, application_id: saved.id });
      }
      localStorage.removeItem('kr_process_started_loan');
      localStorage.removeItem('kr_process_started_combine_loans');
    } catch (e) {
      trackEvent('kr_process_error', { process_name: processName, error_message: e.message || 'save after step 1 failed' });
      // continue to step 2 anyway — same behaviour as the live flow
    } finally {
      setBusy(false);
      setStep(2);
      trackEvent('step_complete', { step: 1 });
      setTimeout(scrollToCard, 0);
    }
  };

  // Step 2: save with attachments, then hand over to the identity provider.
  // H1 mitigation (investigation 5.10.2026): merge Redux application (stage-1 API
  // response) so a partial update does not null out server-set fields.
  const authenticate = async (serviceId) => {
    if (busy) return;
    setBusy(true);
    setLoadingServiceId(serviceId);
    setSubmitError(null);
    let id = savedId;
    try {
      const saved = await saveApplication({ ...application, ...buildPayload(), attachments });
      id = resolveSavedApplicationId(saved, savedId);
      if (id) setSavedId(id);
    } catch (e) {
      trackEvent('kr_process_error', { process_name: processName, error_message: e.message || 'save before authentication failed' });
      id = resolveSavedApplicationId(null, savedId);
    }
    if (!id) {
      setSubmitError(SAVE_FAILED_PHONE_MSG);
      setBusy(false); setLoadingServiceId(null);
      return;
    }
    setBankOpen(false);
    if (AUTH_BYPASS) {
      setBusy(false); setLoadingServiceId(null);
      setStep('done');
      setTimeout(scrollToCard, 0);
      return;
    }
    setTimeout(() => requestLoginToken(serviceId, AUTH_CONTEXT_APPLICATION, id), 500); // redirects the browser
  };

  /* ------------------------------------------------------------ texts */
  const kicker = step === 'done' ? 'Valmis'
    : step === 2 ? 'Vaihe 2 / 2 · Tunnistautuminen'
      : prefilled ? 'Vaihe 1 / 2 · Yrityksesi tiedot' : 'Vaihe 1 / 2 · Yhteystiedot';
  const title = step === 'done' ? '' : step === 2 ? 'Viimeistele hakemus' : prefilled ? 'Täytä tiedot' : 'Aloita hakemus';

  /* ------------------------------------------------------------ render */
  const loanSummary = (
    <div className="kr-loan-summary">
      <div className="kr-ls-head">
        <span className="kr-ls-label">Valitsemasi laina</span>
        {/* Without URL parameters the sliders are open from the start, so no edit link is needed. */}
        {prefilled && (
          <button type="button" className="kr-ls-edit" onClick={() => setEditingLoan((v) => !v)}>{editingLoan ? 'Valmis' : 'Muokkaa'}</button>
        )}
      </div>
      <div className="kr-ls-amount">{fmt(loan.amount)} €</div>
      <div className="kr-ls-meta">
        <strong>{loan.length}</strong> kk laina-aika
        <span className="kr-ls-dot">·</span>
        <strong>{loan.free}</strong> kk lyhennysvapaata
      </div>
      <div className="kr-ls-calc">
        <span>Arvioitu kk-erä <strong>{monthly}</strong></span>
        <span>Yhteensä <strong>{total}</strong></span>
      </div>
      {editingLoan && <div className="kr-ls-sliders"><LoanSliders loan={loan} onChange={changeLoan} /></div>}
      <AiFastTrackChip amount={loan.amount} />
    </div>
  );

  return (
    <>
      <section className="kr-hero">
        <div className="kr-container kr-hero-grid">
          <div className="kr-hero-lead">
            <h1>{prefilled ? 'Olet melkein valmis – täytä yrityksesi tiedot' : 'Hae yrityslainaa nopeasti ja ilman sitoutumista'}</h1>
            <TrustChips items={CHIPS} />
          </div>

          <div className="kr-form-card" ref={cardRef}>
            <div className="kr-form-head">
              <div className="kr-kicker">{kicker}</div>
              {title && <h2>{title}</h2>}
            </div>
            <StepsBar step={step} />

            {step === 1 && (
              <div className="kr-form-body" role="group" aria-label="Vaihe 1">
                {loanSummary}
                <p className="kr-calc-note">Arvio. Lopullinen korko ja kulut näkyvät tarjouksessa ennen sitoutumista.</p>
                <Switch checked={consolidation} onChange={(v) => { markStarted(); setConsolidation(v); }} label="Yhdistelylaina">
                  Haetko yhdistelylainaa nykyisten yrityslainojen korvaamiseksi Kasvurahoituksella?
                </Switch>
                <hr className="kr-hr" />

                <CompanyContactFields fields={fields} setField={setField} blurField={blurField} errors={errors} isValid={isValid} bic={bic} />
                <CampaignCode open={showCampaign} onToggle={() => setShowCampaign((v) => !v)} value={fields.discountCode} onChange={setField('discountCode')} />

                {/* Same three consents as the production form: terms, credit check, AI-assisted processing. */}
                <Consent id="kr-terms" checked={terms} onChange={(v) => { setTerms(v); if (v) setErrors((e) => ({ ...e, terms: undefined })); }} error={errors.terms}>
                  Olen tutustunut Kasvurahoituksen <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">käyttöehtoihin</a> ja hyväksyn ne itseäni ja edustamaani yritystä sitoviksi.
                  Annan suostumuksen henkilötietojeni käyttämiseen luottokelpoisuuden arviointiin luottotietorekisteristä tehtäviin kyselyihin sekä muista saatavilla olevista tietolähteistä.
                  Hyväksyn, että lainahakemusta käsitellään AI-avusteisesti.
                </Consent>

                <button type="button" className="kr-btn kr-btn-primary" id="kr-toStep2" onClick={goStep2} disabled={busy}>
                  {busy ? <span className="kr-spinner" /> : <>Pyydä lainatarjous<ArrowIcon /></>}
                </button>
                <div className="kr-cta-note"><Tick /> Maksuton eikä sido sinua – saat tarjouksen ensin</div>
                <Assurance items={['Tunnistautuminen vasta seuraavassa vaiheessa.', 'Tietosi käsitellään turvallisesti ja luottamuksellisesti.']} />
              </div>
            )}

            {step === 2 && (
              <div className="kr-form-body" role="group" aria-label="Vaihe 2: Tunnistautuminen">
                <p className="kr-auth-intro">
                  Viimeisenä tunnistaudut pankkitunnuksilla tai mobiilivarmenteella. Saat tiedon lainatarjouksesta sähköpostitse ja tekstiviestillä.{' '}
                  <strong>Tarjous on maksuton eikä sido yritystäsi vielä mihinkään.</strong>
                </p>
                {AUTH_BYPASS && <div className="kr-notice">Kehitystila: tunnistautuminen ohitetaan, hakemus tallennetaan mock-palvelimelle.</div>}
                {submitError && <div className="kr-form-error" role="alert">{submitError}</div>}

                {/* Attachments come before authentication: the redirect leaves this page. */}
                <AttachmentsBlock open={showAttachments} onToggle={() => setShowAttachments((v) => !v)} attachments={attachments} onAdd={addFiles} onRemove={removeFile} />
                <AuthButton busy={busy} onClick={() => setBankOpen(true)} />

                <div className="kr-cta-note"><LockIcon /> Vahva tunnistautuminen, tiedot salataan</div>
                <Assurance items={['Saat tarjouksen ennen kuin teet päätöksen.', 'Asiakaspalvelumme auttaa tarvittaessa, ark. 9–16.']} />
                <button type="button" className="kr-back-link" onClick={() => { setStep(1); setTimeout(scrollToCard, 0); }} disabled={busy}>← Takaisin tietoihin</button>
              </div>
            )}

            {step === 'done' && (
              <div className="kr-success" role="status" aria-live="polite">
                <SuccessIcon />
                <h2>Kiitos – hakemuksesi on vastaanotettu!</h2>
                {savedId && <p className="kr-success-ref">Hakemusnumero <strong>{savedId}</strong>{fields.companyName ? ` · ${fields.companyName}` : ''} · {fmt(loan.amount)} € / {loan.length} kk</p>}
                <ol className="kr-next-steps">
                  <li>Käsittelemme hakemuksesi pikimmiten, usein jo saman päivän aikana.</li>
                  <li>Saat lainatarjouksen sähköpostiin <strong>{fields.email}</strong> ja tekstiviestillä.</li>
                  <li>Tarjous ei sido sinua – päätät vasta sen nähtyäsi.</li>
                </ol>
                <p><strong>Kysyttävää?</strong> Soita <a href="tel:+358968298003">09 6829 8003</a> (ark. 9–16).</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <BankSelectionPanel open={bankOpen} onClose={() => setBankOpen(false)} onBankSelect={authenticate} disabled={busy} loadingServiceId={loadingServiceId} />

      {/* Mobile: the sticky button brings the user to the form instead of submitting something they cannot see. */}
      {step !== 'done' && <StickyCta visible={stickyVisible} label={step === 2 ? 'Viimeistele tunnistautumalla' : 'Siirry hakemukseen'} onClick={scrollToCard} />}
    </>
  );
}

const mapStateToProps = (state) => ({ application: state.application, offer: state.offer });

const mapDispatchToProps = (dispatch) => ({
  updateApplication: (application) => dispatch(createUpdateApplicant(application)),
  getOffer: (amt, len, due, free) => dispatch(GetFetchOffer(amt, len, due, free)),
  fetchYtj: (bic) => dispatch(getYtjInformation(bic)),
  saveApplication: (application) => dispatch(createSaveApplication(application)),
  requestLoginToken: (serviceId, authContext, applicationId) => dispatch(createLoginToken(serviceId, authContext, applicationId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BecomeApplicantV2Process);
